import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { isSameDay } from "date-fns";
import { usePrimaryFetchedData } from "./PrimaryFetchedDataContext";
import { ItemSnapshot, Shipment } from "../types/DB2";
import { AlertColor } from "@mui/material";
import { useUserData } from "./UserDataFetcher";
import { isVerboseLoggingEnabled } from "../utils/debug";

type LoadingStatusType = "loading" | "unloading";

interface SnackbarOptions {
  message: string;
  severity?: AlertColor;
  duration?: number;
}

interface UserControlsContextType {
  loadingStatus: LoadingStatusType;
  unloadLocation: string;
  setLoadingStatus: React.Dispatch<React.SetStateAction<LoadingStatusType>>;
  setUnloadLocation: React.Dispatch<React.SetStateAction<string>>;
  actionStatus: string;
  setActionStatus: React.Dispatch<React.SetStateAction<string>>;
  selectedShipment: string;
  possibleLocations: Array<string>;
  selectedShipmentIndex: number;
  setSelectedShipmentIndex: (newShipmentIndex: number) => void;
  recentScannedItemsIds: string[];
  updateRecentScannedItemsIds: (id: string) => void;
  selectedShipmentObject: Shipment | null;
  // Snackbar controls
  snackbarOpen: boolean;
  snackbarOptions: SnackbarOptions;
  showSnackbar: (opts: SnackbarOptions) => void;
  hideSnackbar: () => void;
  lastScannedItem: string | null;
  setLastScannedItem: (item: string | null) => void;
  selectedShipmentItems: Array<ItemSnapshot>;
  processedGroups: Record<string, ItemSnapshot[]>;
  unprocessedGroups: Record<string, ItemSnapshot[]>;
  lookupItemInSelectedShipment: (
    itemId: string,
    options?: { allowSuffixMatch?: boolean }
  ) => ItemSnapshot | undefined;
  autoStartScanner: boolean;
  setAutoStartScanner: React.Dispatch<React.SetStateAction<boolean>>;
  possibleShipmentIds: Array<string>;
}

const UserControlsContext = createContext<UserControlsContextType>({
  loadingStatus: "loading",
  unloadLocation: "",
  setLoadingStatus: () => {},
  setUnloadLocation: () => {},
  actionStatus: "unloading",
  setActionStatus: () => {},
  selectedShipment: "",
  selectedShipmentObject: null,
  possibleLocations: [],
  selectedShipmentIndex: 0,
  setSelectedShipmentIndex: () => {},
  recentScannedItemsIds: [],
  updateRecentScannedItemsIds: () => {},
  // snackbar defaults
  snackbarOpen: false,
  snackbarOptions: { message: "", severity: "info", duration: 4000 },
  showSnackbar: () => {},
  hideSnackbar: () => {},
  lastScannedItem: null,
  setLastScannedItem: (item: string | null) => {},
  selectedShipmentItems: [],
  processedGroups: {},
  unprocessedGroups: {},
  lookupItemInSelectedShipment: (_itemId: string) => undefined,
  autoStartScanner: false,
  setAutoStartScanner: () => {},
  possibleShipmentIds: [],
});

export const useUserControls = () => {
  const context = useContext(UserControlsContext);
  if (context === undefined) {
    throw new Error("UserControlsContext creation failed");
  }
  return context;
};

export const UserControlsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selectedShipmentIndex, _setSelectedShipmentIndex] =
    useState<number>(-1);
  const [lastScannedItem, setLastScannedItem] = useState<string | null>(null);

  const { shipments, orders } = usePrimaryFetchedData();
  const userData = useUserData();
  const verbose = isVerboseLoggingEnabled();

  const [loadingStatus, setLoadingStatus] =
    useState<LoadingStatusType>("unloading");
  const [unloadLocation, setUnloadLocation] = useState<string>("");
  const [actionStatus, setActionStatus] = useState<string>("unloading");
  const [recentScannedItemsIds, setRecentScannedItemsIds] = useState<string[]>(
    []
  );
  const [autoStartScanner, setAutoStartScanner] = useState<boolean>(false);
  const markSelectionPhase = useCallback(
    (
      phase: string,
      shipmentId: string | null,
      meta: Record<string, unknown> = {}
    ) => {
      if (typeof window === "undefined" || typeof performance === "undefined") {
        return;
      }
      try {
        const diag = (window as any).__cabinetSelectionDiag;
        if (!diag || typeof diag !== "object") {
          return;
        }
        const {
          shipmentId: targetId,
          startedAt,
          loggedPhases = {},
          timerLabel,
        } = diag as {
          shipmentId?: string | null;
          startedAt?: number;
          loggedPhases?: Record<string, boolean>;
          timerLabel?: string | null;
        };
        const matchesTarget =
          !shipmentId ||
          !targetId ||
          shipmentId === targetId ||
          (!targetId && shipmentId);
        if (!matchesTarget) {
          return;
        }
        if (loggedPhases[phase]) {
          return;
        }
        const now = performance.now?.() ?? Date.now();
        const elapsed = now - (startedAt ?? 0);
        diag.loggedPhases = {
          ...loggedPhases,
          [phase]: true,
        };
        if (
          phase === "selection-committed" &&
          timerLabel &&
          typeof console !== "undefined" &&
          typeof console.timeEnd === "function"
        ) {
          console.timeEnd(timerLabel);
        } else if (
          timerLabel &&
          typeof console !== "undefined" &&
          typeof console.timeLog === "function"
        ) {
          console.timeLog(timerLabel, phase);
        }
        console.info("[SelectionDiagnostics] phase complete", {
          phase,
          shipmentId: shipmentId ?? targetId ?? "<unknown>",
          elapsedMs: Number(elapsed.toFixed(1)),
          ...meta,
        });
      } catch (error) {
        console.warn("[SelectionDiagnostics] phase log failed", {
          phase,
          error,
        });
      }
    },
    []
  );

  // Helper to safely convert Firestore Timestamp/string/number -> Date
  const toDate = (value: any): Date | null => {
    try {
      if (!value) return null;
      if (typeof value.toDate === "function") return value.toDate();
      if (value instanceof Date) return value;
      if (typeof value === "string" || typeof value === "number")
        return new Date(value as any);
      return null;
    } catch {
      return null;
    }
  };

  // Default the selected shipment to align with Home: pick the most recent shipment day on/before today,
  // else the earliest available day. Only applies when no selection yet.
  useEffect(() => {
    if (!shipments || shipments.length === 0) return;
    if (selectedShipmentIndex !== -1) return;
    const today = new Date();
    const days = shipments
      .map((s) => toDate((s as any).shipmentDate))
      .filter((d): d is Date => !!d)
      .map((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime());
    if (days.length === 0) return;
    const unique = Array.from(new Set(days)).sort((a, b) => a - b);
    const todayKey = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const onOrBefore = unique.filter((t) => t <= todayKey);
    const targetKey = onOrBefore.length ? Math.max(...onOrBefore) : Math.min(...unique);

    // Find first shipment that falls on the target day
    const targetIndex = shipments.findIndex((s) => {
      const d = toDate((s as any).shipmentDate);
      return d ? isSameDay(d, new Date(targetKey)) : false;
    });
    if (targetIndex >= 0) {
      _setSelectedShipmentIndex(targetIndex);
      if (verbose) {
        try {
          console.info('[UserControls] auto-selected default shipment', {
            targetIndex,
            shipmentId: shipments[targetIndex]?.id,
          });
        } catch {}
      }
    }
  }, [shipments, selectedShipmentIndex, verbose]);

  useEffect(() => {
    if (userData?.role === "dealer") return;
    if (selectedShipmentIndex >= shipments.length) {
      _setSelectedShipmentIndex(-1);
    }
  }, [shipments.length, selectedShipmentIndex, userData?.role]);

  const possibleShipmentIds = useMemo(() => {
    let idsToReturn: Array<string> = [];
    if (userData?.role === "dealer") {
      orders.forEach((order) => {
        idsToReturn.push(...(order.shipmentIds || []));
      });
    } else if (
      userData?.role === "manufacturer" ||
      userData?.role === "driver"
    ) {
      idsToReturn = shipments.map((shipment) => shipment.id);
    }
    const uniqueIds = Array.from(new Set(idsToReturn));
    return uniqueIds;
  }, [orders, shipments, userData?.role]);

  useEffect(() => {
    if (userData?.role !== "dealer") return;
    if (selectedShipmentIndex >= possibleShipmentIds.length) {
      _setSelectedShipmentIndex(-1);
    }
  }, [possibleShipmentIds.length, selectedShipmentIndex, userData?.role]);

  // Snackbar state & helpers
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarOptions, setSnackbarOptions] = useState<SnackbarOptions>({
    message: "",
    severity: "info",
    duration: 4000,
  });

  const showSnackbar = (opts: SnackbarOptions) => {
    setSnackbarOptions((prev) => ({ ...prev, ...opts }));
    setSnackbarOpen(true);
  };
  const hideSnackbar = () => setSnackbarOpen(false);

  const selectedShipmentObject = shipments[selectedShipmentIndex] || null;

  const possibleLocations = useMemo(() => {
    if (!selectedShipmentObject) return [];
    let possibleLocationsToReturn: string[] = [];
    Object.values(selectedShipmentObject.ordersSnapshot || {}).forEach(
      (orderSnapshot) => {
        Object.values(orderSnapshot.includedItemsSnapshot || {}).forEach(
          (item) => {
            if (item.destination) {
              possibleLocationsToReturn.push(item.destination);
            }
          }
        );
      }
    );
    possibleLocationsToReturn = [...new Set(possibleLocationsToReturn)];
    // Default location: choose first if current selection is empty or not in list
    if (possibleLocationsToReturn.length > 0) {
      const inList = possibleLocationsToReturn.includes(unloadLocation);
      if (!inList) {
        setUnloadLocation(possibleLocationsToReturn[0]);
        if (verbose) {
          try {
            console.info('[UserControls] defaulted unloadLocation', {
              chosen: possibleLocationsToReturn[0],
              count: possibleLocationsToReturn.length,
            });
          } catch {}
        }
      }
    }
    return possibleLocationsToReturn;
  }, [shipments, selectedShipmentIndex, unloadLocation, verbose]);

  const selectedShipmentItems = useMemo(() => {
    if (!selectedShipmentObject) return [];
    if (!selectedShipmentObject.ordersSnapshot) return [];
    if (Object.values(selectedShipmentObject.ordersSnapshot).length === 0)
      return [];
    const items = Object.values(selectedShipmentObject.ordersSnapshot).flatMap(
      (orderSnapshot) => Object.values(orderSnapshot.includedItemsSnapshot)
    );
    if (verbose) {
      try {
        console.log('[UserControls] selectedShipmentItems computed', {
          shipmentId: selectedShipmentObject.id,
          ordersCount: Object.keys(selectedShipmentObject.ordersSnapshot || {}).length,
          itemsCount: items.length,
          sample: items.slice(0, 5).map((i:any) => ({ id: i.id, status: i.status, currentProcessed: i.currentProcessed, destination: i.destination })),
        });
      } catch (_) {}
    }
    return items;
  }, [shipments, selectedShipmentObject, verbose]);

  const selectedShipment = useMemo(() => {
    if (userData?.role === "dealer") {
      return possibleShipmentIds[selectedShipmentIndex] || "";
    }
    return selectedShipmentObject?.id || "";
  }, [
    userData?.role,
    possibleShipmentIds,
    selectedShipmentIndex,
    selectedShipmentObject,
  ]);

  if (verbose) {
    try {
      console.log('[UserControls] selection state', {
        role: userData?.role,
        selectedShipmentIndex,
        selectedShipment,
        hasSelectedShipmentObject: !!selectedShipmentObject,
        possibleShipmentIdsCount: possibleShipmentIds.length,
        unloadLocation,
        loadingStatus,
      });
    } catch(_) {}
  }

  useEffect(() => {
    if (!selectedShipment) return;
    markSelectionPhase("selection-committed", selectedShipment, {
      index: selectedShipmentIndex,
    });
  }, [selectedShipment, selectedShipmentIndex, markSelectionPhase]);

  useEffect(() => {
    if (!selectedShipmentObject) return;
    const shipmentId = selectedShipmentObject.id ?? selectedShipment ?? null;
    if (!shipmentId) return;
    markSelectionPhase("shipment-object-ready", shipmentId, {
      orders: Object.keys(selectedShipmentObject.ordersSnapshot || {}).length,
    });
  }, [selectedShipmentObject, selectedShipment, markSelectionPhase]);

  // Build scanned/unscanned groups depending on role
  const { processedGroups, unprocessedGroups } = React.useMemo(() => {
    const processed: Record<string, ItemSnapshot[]> = {};
    const unprocessed: Record<string, ItemSnapshot[]> = {};
    const userRole = userData?.role;

    const buildGroupsFromShipment = (shipment: Shipment | null | undefined) => {
      if (!shipment?.ordersSnapshot) return;
      if (verbose) {
        console.log('[UserControls] building groups from shipment', {
          role: userRole,
          shipmentId: shipment.id,
          loadingStatus,
          unloadLocation,
          ordersCount: Object.keys(shipment.ordersSnapshot || {}).length,
        });
      }
      Object.entries(shipment.ordersSnapshot).forEach(
        ([orderId, orderSnapshot]) => {
          const allItems = Object.values(
            orderSnapshot.includedItemsSnapshot || {}
          ) as ItemSnapshot[];
          if (allItems.length === 0) {
            processed[orderId] = [];
            unprocessed[orderId] = [];
            return;
          }

          const beforeFilterCount = allItems.length;
          const beforeStatusCount: Record<string, number> = {};
          const afterStatusCount: Record<string, number> = {};
          const proc: ItemSnapshot[] = [];
          const unproc: ItemSnapshot[] = [];
          const droppedByLocation: ItemSnapshot[] = [];

          let beforeProcessed = 0;
          let beforeUnprocessed = 0;
          let afterFilterCount = 0;
          const shouldFilterByLocation =
            loadingStatus !== "loading" && Boolean(unloadLocation);

          for (const item of allItems) {
            beforeStatusCount[item.status] =
              (beforeStatusCount[item.status] || 0) + 1;
            if (item.currentProcessed) {
              beforeProcessed += 1;
            } else {
              beforeUnprocessed += 1;
            }

            const matchesLocation =
              !shouldFilterByLocation ||
              item.destination === unloadLocation;

            if (matchesLocation) {
              afterStatusCount[item.status] =
                (afterStatusCount[item.status] || 0) + 1;
              afterFilterCount += 1;
              if (item.currentProcessed) {
                proc.push(item);
              } else {
                unproc.push(item);
              }
            } else if (item.currentProcessed) {
              droppedByLocation.push(item);
            }
          }

          processed[orderId] = proc;
          unprocessed[orderId] = unproc;

          if (verbose) {
            try {
              console.log('[UserControls] order grouping', {
                orderId,
                beforeFilterCount,
                afterFilterCount,
                loadingStatus,
                unloadLocation,
                beforeProcessed,
                beforeUnprocessed,
                processedCount: proc.length,
                unprocessedCount: unproc.length,
                beforeStatusCount,
                afterStatusCount,
                droppedByLocationSample: droppedByLocation
                  .slice(0, 5)
                  .map((i: any) => ({
                    id: i.id,
                    status: i.status,
                    currentProcessed: i.currentProcessed,
                    destination: i.destination,
                  })),
                processedSample: proc.slice(0, 5).map((i: any) => ({
                  id: i.id,
                  status: i.status,
                  currentProcessed: i.currentProcessed,
                  destination: i.destination,
                })),
                unprocessedSample: unproc.slice(0, 5).map((i: any) => ({
                  id: i.id,
                  status: i.status,
                  currentProcessed: i.currentProcessed,
                  destination: i.destination,
                })),
              });
            } catch (_) {}
          }
        }
      );
    };

    if (userRole === "dealer") {
      if (selectedShipmentObject) {
        buildGroupsFromShipment(selectedShipmentObject);
      } else {
        orders.forEach((order) => {
          if (selectedShipment && !order.shipmentIds?.includes(selectedShipment))
            return;
          let items = Object.values(order.itemsSnapshot || {}) as ItemSnapshot[];
          if (loadingStatus !== "loading" && unloadLocation) {
            items = items.filter((i) => i.destination === unloadLocation);
          }
          processed[order.id] = items.filter((i) => !!i.currentProcessed);
          unprocessed[order.id] = items.filter((i) => !i.currentProcessed);
        });
      }
    } else {
      buildGroupsFromShipment(selectedShipmentObject);
    }

    if (verbose) {
      try {
        const processedTotal = Object.values(processed).reduce(
          (n, arr) => n + arr.length,
          0
        );
        const unprocessedTotal = Object.values(unprocessed).reduce(
          (n, arr) => n + arr.length,
          0
        );
        console.log('[UserControls] groups summary', {
          role: userRole,
          loadingStatus,
          unloadLocation,
          processedTotal,
          unprocessedTotal,
          ordersWithProcessed: Object.keys(processed).filter(
            (k) => (processed as any)[k]?.length > 0
          ),
        });
      } catch (_) {}
    }

    return { processedGroups: processed, unprocessedGroups: unprocessed };
  }, [
    orders,
    selectedShipmentObject,
    userData?.role,
    selectedShipment,
    loadingStatus,
    unloadLocation,
    selectedShipmentIndex,
    verbose,
  ]);

  useEffect(() => {
    if (!selectedShipment) return;
    const processedCount = Object.keys(processedGroups).length;
    const unprocessedCount = Object.keys(unprocessedGroups).length;
    markSelectionPhase("groups-ready", selectedShipment, {
      processedGroups: processedCount,
      unprocessedGroups: unprocessedCount,
    });
  }, [processedGroups, unprocessedGroups, selectedShipment, markSelectionPhase]);

  const updateRecentScannedItemsIds = (id: string) => {
    setRecentScannedItemsIds((prev) => [...prev, id]);
  };

  const setSelectedShipmentIndex = (newShipmentIndex: number) => {
    _setSelectedShipmentIndex(newShipmentIndex);
  };

  const lookupItemInSelectedShipment = (
    itemId: string,
    _options?: { allowSuffixMatch?: boolean }
  ) => {
    if (!selectedShipmentObject) return undefined;

    const raw = typeof itemId === 'string' ? itemId : String(itemId ?? '');
    const trimmed = raw.trim();
    const normalized = trimmed.replace(/\s+/g, '');
    if (!trimmed) return undefined;

    const ordersMap = selectedShipmentObject.ordersSnapshot || {};

    // Search each order's included items and enrich the found item with an orderId if missing.
    for (const [orderKey, orderSnapshot] of Object.entries(ordersMap)) {
      const itemsMap = orderSnapshot?.includedItemsSnapshot || {};
      for (const it of Object.values(itemsMap)) {
        if (!it) continue;
        const candidate = typeof (it as any).barcodeId === 'string' ? (it as any).barcodeId : '';
        if (!candidate) continue;
        const cTrim = candidate.trim();
        const cNorm = cTrim.replace(/\s+/g, '');
        const isMatch = cTrim === trimmed || cNorm === normalized;
        if (isMatch) {
          const ensuredOrderId = (it as any).orderId || (orderSnapshot as any).orderId || orderKey;
          return { ...(it as ItemSnapshot), orderId: ensuredOrderId } as ItemSnapshot;
        }
      }
    }
    return undefined;
  };

  return (
    <UserControlsContext.Provider
      value={{
        autoStartScanner,
        setAutoStartScanner,
        selectedShipmentItems,
        recentScannedItemsIds,
        selectedShipmentIndex,
        setSelectedShipmentIndex,
        loadingStatus,
        unloadLocation,
        setLoadingStatus,
        setUnloadLocation,
        actionStatus,
        setActionStatus,
        selectedShipment,
        possibleLocations,
        updateRecentScannedItemsIds,
        selectedShipmentObject,
        processedGroups,
        unprocessedGroups,
        // snackbar
        snackbarOpen,
        snackbarOptions,
        showSnackbar,
        hideSnackbar,
        lastScannedItem,
        setLastScannedItem,
        lookupItemInSelectedShipment,
        possibleShipmentIds,
      }}
    >
      {children}
    </UserControlsContext.Provider>
  );
};
